import { useState, useEffect } from 'react';
import { useLogAnalyticsSearchJobCalc } from '../../hooks/useCalculate';
import { useAnalytics } from '../../hooks/useAnalytics';
import { Disclaimer } from '../shared/Disclaimer';
import { FeedbackModal } from '../shared/FeedbackModal';
import { ResourcesSection } from '../shared/ResourcesSection';
import { ShareButton } from '../shared/ShareButton';
import type { LogAnalyticsSearchJobRequest } from '../../api/types';
import { FieldTooltip } from '../shared/FieldTooltip';

export default function LogAnalyticsSearchJobCalc() {
  const [inputs, setInputs] = useState<LogAnalyticsSearchJobRequest>({
    dailyIngestionGB: 35,
    searchRangeDays: 30,
    estimatedResultGB: 1,
  });

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const set = (partial: Partial<LogAnalyticsSearchJobRequest>) =>
    setInputs((prev) => ({ ...prev, ...partial }));

  const { data, isLoading, isError } = useLogAnalyticsSearchJobCalc(inputs);
  const { trackCalculatorUsed } = useAnalytics();
  useEffect(() => {
    if (data?.monthly_estimate) {
      trackCalculatorUsed({ calculator: 'log-analytics', result_gbp: data.monthly_estimate })
    }
  }, [data?.monthly_estimate])
  const gbScanned = inputs.dailyIngestionGB * inputs.searchRangeDays;

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_380px] gap-8 w-full">
      {/* ── Left: Controls ── */}
      <div className="flex flex-col gap-6">
        <div className={`bg-surface-container-high micro-gradient ghost-border rounded-lg p-6 transition-opacity ${isLoading ? 'opacity-70' : ''}`}>
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-6">
            <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>search</span>
            Search Job Configuration
          </h2>

          {/* Daily ingestion rate */}
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex justify-between items-end">
              <div>
                <label className="text-[11px] font-mono text-outline uppercase tracking-wider flex items-center">Daily Ingestion Rate<FieldTooltip content="Search Jobs scan all data in the time range — the total GB charged equals daily rate × number of days, regardless of how many rows match your query." /></label>
                <p className="text-[10px] text-outline/60 mt-0.5">Used to calculate GB scanned across your time range</p>
              </div>
              <span className="text-xl font-bold font-mono text-primary">{inputs.dailyIngestionGB} GB/day</span>
            </div>
            <input
              type="range"
              min={1} max={500} step={1}
              value={inputs.dailyIngestionGB}
              onChange={(e) => set({ dailyIngestionGB: Number(e.target.value) })}
              className="w-full h-1 bg-surface-container-highest rounded-lg appearance-none cursor-pointer accent-primary"
            />
          </div>

          {/* Search time range */}
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex justify-between items-end">
              <div>
                <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Search Time Range</label>
                <p className="text-[10px] text-outline/60 mt-0.5">Number of days of logs to scan</p>
              </div>
              <span className="text-xl font-bold font-mono text-primary">{inputs.searchRangeDays} days</span>
            </div>
            <input
              type="range"
              min={1} max={365} step={1}
              value={inputs.searchRangeDays}
              onChange={(e) => set({ searchRangeDays: Number(e.target.value) })}
              className="w-full h-1 bg-surface-container-highest rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="flex justify-between text-[10px] text-outline font-mono">
              <span>1 day</span>
              <span className="text-outline/60 font-bold">{gbScanned.toLocaleString()} GB scanned total</span>
              <span>365 days</span>
            </div>
          </div>

          {/* Estimated result size */}
          <div className="flex flex-col gap-2 mb-2">
            <label className="text-[11px] font-mono text-outline uppercase tracking-wider flex items-center">Estimated Result Size (GB)<FieldTooltip content="Matching rows are written to a temporary _SRCH table and billed at the Analytics Logs ingestion rate. If you're unsure, start at 1 GB — most search jobs produce small result sets." /></label>
            <p className="text-[10px] text-outline/60">Results are ingested into a temporary _SRCH table and billed at Analytics Logs rate</p>
            <input
              type="number"
              min={0}
              step={0.1}
              value={inputs.estimatedResultGB}
              onChange={(e) => set({ estimatedResultGB: Math.max(0, Number(e.target.value)) })}
              className="bg-surface-container-lowest border border-outline-variant p-3 text-sm font-semibold focus:outline-none focus:border-primary text-on-surface w-full"
            />
          </div>

          {/* Info box */}
          <div className="mt-4 flex items-start gap-3 bg-surface-container-lowest/40 border border-outline-variant/40 p-3 rounded-sm">
            <span className="material-symbols-outlined text-outline text-base shrink-0">info</span>
            <p className="text-[10px] text-outline leading-relaxed">
              Search Jobs scan <strong>all data</strong> within the time range — you're charged for total GB scanned, not just matching rows. Narrow the time range to reduce cost.
            </p>
          </div>
        </div>
      </div>

      {/* ── Right: Results ── */}
      <div className="flex flex-col gap-6">
        <div className="sticky top-[72px] flex flex-col gap-6">
          <div className="bg-surface-container-high micro-gradient border-l-2 border-primary rounded-lg overflow-hidden">
            <div className="p-6">
              <h3 className="font-mono text-[11px] text-outline uppercase tracking-widest mb-2">Search Job Total Cost</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold font-headline">
                  {isLoading ? '...' : isError ? 'Error' : `£${data?.monthly_estimate.toFixed(2)}`}
                </span>
                <button
                  type="button"
                  onClick={() => setFeedbackOpen(true)}
                  title="Report an issue with this estimate"
                  className="ml-1 transition-colors"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  <span className="material-symbols-outlined text-[18px]">help</span>
                </button>
              </div>
              {data && (
                <p className="text-[11px] text-outline mt-2 font-mono">{data.gb_scanned.toLocaleString()} GB scanned</p>
              )}
            </div>
            <div className="bg-surface-container-lowest/50 p-6 space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant flex items-center gap-2">
                <span className="material-symbols-outlined text-sm">list_alt</span>
                Cost Breakdown
              </h4>
              <ul className="space-y-3">
                {data?.breakdown.map((item, idx) => (
                  <li key={idx} className="flex flex-col gap-0.5">
                    <div className="flex justify-between items-center font-mono text-xs">
                      <span className="text-outline">{item.label}</span>
                      <span className="font-bold">£{item.value.toFixed(3)}</span>
                    </div>
                    {item.note && <p className="text-[10px] text-outline/60 italic">{item.note}</p>}
                  </li>
                ))}
                {!data && !isLoading && <p className="text-[10px] text-outline text-center py-4">Adjust inputs to see pricing</p>}
                {data && (
                  <li className="pt-3 border-t border-outline-variant flex justify-between items-center font-mono text-sm">
                    <span className="font-bold">Total</span>
                    <span className="font-bold text-primary">£{data.monthly_estimate.toFixed(2)}</span>
                  </li>
                )}
              </ul>
            </div>
          </div>

          {/* Restore comparison — amber warning box */}
          {data?.restore_comparison && (
            <div className="bg-[#e8b000]/10 border border-[#e8b000]/40 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="material-symbols-outlined text-[#e8b000]" style={{ fontVariationSettings: "'FILL' 1" }}>compare_arrows</span>
                <h4 className="text-sm font-bold text-[#e8b000]">Compare: Restore vs Search Job</h4>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-[11px] text-outline">Search Job (this estimate)</span>
                  <span className="text-sm font-mono font-bold text-[#107c10]">£{data.monthly_estimate.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[11px] text-outline">Restore (same data, 1 day)</span>
                  <span className="text-sm font-mono font-bold text-[#e8b000]">£{data.restore_comparison.restore_cost_1_day.toFixed(2)}</span>
                </div>
                {data.restore_comparison.minimum_applied && (
                  <div className="bg-[#e8b000]/20 border border-[#e8b000]/30 p-3 rounded-sm">
                    <p className="text-[10px] text-[#e8b000] leading-relaxed flex items-start gap-2">
                      <span className="material-symbols-outlined text-[14px] shrink-0 mt-0.5">warning</span>
                      {data.restore_comparison.minimum_note}
                    </p>
                  </div>
                )}
                <div className="pt-2 border-t border-[#e8b000]/20">
                  <p className="text-[11px] text-outline">
                    Search Job saves{' '}
                    <span className="font-bold text-[#107c10]">£{data.restore_comparison.search_job_saving.toFixed(2)}</span>{' '}
                    vs Restore for this query.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Recommendation */}
          {data?.recommendation && (
            <div className="bg-[#107c10]/10 border border-[#107c10]/30 rounded-lg p-4 flex items-start gap-3">
              <span className="material-symbols-outlined text-[#107c10] text-base shrink-0">check_circle</span>
              <p className="text-[11px] text-outline leading-relaxed">{data.recommendation}</p>
            </div>
          )}

          <div className="bg-surface-container-lowest/50 border border-outline-variant p-4 text-[10px] text-outline leading-relaxed">
            <Disclaimer />
          </div>
          <div className="pb-2">
            <ShareButton inputs={inputs} result={data ?? null} service="log-analytics" />
          </div>
          <ResourcesSection service="log-analytics" />
        </div>
      </div>

      {feedbackOpen && (
        <FeedbackModal
          service="log-analytics/search-job"
          estimateValue={data ? `£${data.monthly_estimate.toFixed(2)}` : 'N/A'}
          onClose={() => setFeedbackOpen(false)}
        />
      )}
    </div>
  );
}
